import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Loader2, Video, VideoOff, X } from 'lucide-react'
import type Hls from 'hls.js'

/** Backend's own NETRADYNE_LIVE_SITES (serving/app.py) -- the four fleets sharing one
 *  Netradyne tenant. Kept in sync by hand; that set isn't exposed over the API. */
export type NetradyneSite = 'ultratech' | 'mbmt' | 'star_cement' | 'gti' | 'jm_baxi'

export interface NetradyneCameraButtonProps {
  site: NetradyneSite
  vehicleNumber: string
  /** Netradyne's own numeric alert id -- only present on a Netradyne-sourced alert card.
   *  Clip retrieval has nothing to request without one, so the Clip tab stays disabled
   *  when this is omitted (e.g. on a plain fleet card, or an alert this codebase derived
   *  from our own telemetry rather than Netradyne). */
  alertId?: number | null
  /** Positions the trigger icon itself -- e.g. "absolute right-2 top-2 hidden
   *  group-hover:flex" to float over a card the same way the existing Play / Mark
   *  breakdown icons do (see busOps/BusOpsShell.tsx), or omitted to sit inline. */
  className?: string
}

type Tab = 'live' | 'clip'

interface FetchState {
  loading: boolean
  error: string | null
  hlsUrl: string | null
  raw: unknown
}

const IDLE: FetchState = { loading: false, error: null, hlsUrl: null, raw: null }

/** Confirmed live against the real Netradyne response (netradyne_live_stream, both
 *  Ultratech and MBMT): {"data": {"liveStreamingHlsUrls": [{"url": "...", "position": 0}]}}.
 *  netradyne_alert_clip's shape is still unconfirmed (501 placeholder) -- the
 *  hlsUrl/url/streamUrl guesses stay as a fallback for whenever that one goes live and
 *  turns out to use a different field. */
function extractHlsUrl(json: unknown): string | null {
  if (!json || typeof json !== 'object') return null
  const j = json as Record<string, unknown>
  const data = j.data && typeof j.data === 'object' ? (j.data as Record<string, unknown>) : undefined
  const liveUrls = data?.liveStreamingHlsUrls
  if (Array.isArray(liveUrls)) {
    const first = liveUrls.find((u): u is { url: string } => !!u && typeof u === 'object' && typeof (u as { url?: unknown }).url === 'string')
    if (first) return first.url
  }
  const candidates = [j.hlsUrl, j.url, j.streamUrl, data?.hlsUrl, data?.url]
  for (const c of candidates) {
    if (typeof c === 'string' && c) return c
  }
  return null
}

/** The backend passes Netradyne's error body straight through (serving/app.py's
 *  _netradyne_post), and Netradyne's own error shape is itself
 *  {"error": {"message": "...", "messageCode": "...", ...}} -- an OBJECT, not a string,
 *  confirmed live (status 400, "Live stream URL is currently in use. Please try again
 *  later.", messageCode error.live.stream.request.already.streaming). String(j.error) on
 *  that object renders literally as "[object Object]"; pull .message out of it instead. */
function extractErrorMessage(json: unknown, fallback: string): string {
  if (json && typeof json === 'object') {
    const err = (json as Record<string, unknown>).error
    if (typeof err === 'string' && err) return err
    if (err && typeof err === 'object') {
      const msg = (err as Record<string, unknown>).message
      if (typeof msg === 'string' && msg) return msg
    }
  }
  return fallback
}

export function NetradyneCameraButton({ site, vehicleNumber, alertId, className }: NetradyneCameraButtonProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>('live')
  const [live, setLive] = useState<FetchState>(IDLE)
  const [clip, setClip] = useState<FetchState>(IDLE)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const hlsRef = useRef<Hls | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onDocMouseDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [open])

  const activeUrl = tab === 'live' ? live.hlsUrl : clip.hlsUrl

  useEffect(() => {
    if (!open || !activeUrl || !videoRef.current) return
    const video = videoRef.current
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = activeUrl
      return () => { video.removeAttribute('src'); video.load() }
    }
    let cancelled = false
    void import('hls.js').then(({ default: HlsCtor }) => {
      if (cancelled || !HlsCtor.isSupported()) return
      hlsRef.current?.destroy()
      const hls = new HlsCtor()
      hlsRef.current = hls
      hls.loadSource(activeUrl)
      hls.attachMedia(video)
    })
    return () => {
      cancelled = true
      hlsRef.current?.destroy()
      hlsRef.current = null
    }
  }, [open, activeUrl])

  async function loadLive() {
    setLive({ ...IDLE, loading: true })
    try {
      const r = await fetch('/api/netradyne/live-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site, vehicle_number: vehicleNumber }),
      })
      const j = await r.json().catch(() => ({}))
      const url = r.ok ? extractHlsUrl(j) : null
      setLive({
        loading: false,
        error: r.ok ? (url ? null : 'No playable URL in the response') : extractErrorMessage(j, String(r.status)),
        hlsUrl: url,
        raw: j,
      })
    } catch (e) {
      setLive({ loading: false, error: e instanceof Error ? e.message : 'request failed', hlsUrl: null, raw: null })
    }
  }

  async function loadClip() {
    if (alertId == null) return
    setClip({ ...IDLE, loading: true })
    try {
      const r = await fetch('/api/netradyne/alert-clip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site, vehicle_number: vehicleNumber, alert_id: alertId }),
      })
      const j = await r.json().catch(() => ({}))
      const url = r.ok ? extractHlsUrl(j) : null
      setClip({
        loading: false,
        error: r.ok ? (url ? null : 'No playable URL in the response') : extractErrorMessage(j, String(r.status)),
        hlsUrl: url,
        raw: j,
      })
    } catch (e) {
      setClip({ loading: false, error: e instanceof Error ? e.message : 'request failed', hlsUrl: null, raw: null })
    }
  }

  function pick(t: Tab) {
    setTab(t)
    if (t === 'live' && live === IDLE) void loadLive()
    if (t === 'clip' && alertId != null && clip === IDLE) void loadClip()
  }

  function toggle() {
    setOpen((wasOpen) => {
      const next = !wasOpen
      if (next && live === IDLE) void loadLive()
      return next
    })
  }

  const state = tab === 'live' ? live : clip

  return (
    <div className={className}>
      <div ref={wrapRef} className="relative inline-block">
        <button
          type="button"
          title="Netradyne camera"
          onClick={(e) => { e.stopPropagation(); toggle() }}
          className={clsx(
            'relative flex h-7 w-7 items-center justify-center rounded-full border transition-colors',
            open
              ? 'border-[#DC2626] bg-[#DC2626] text-white'
              : 'border-white/10 bg-[#0F172A]/90 text-white hover:border-[#DC2626]/50 hover:bg-[#0F172A]',
          )}
        >
          <Video size={12} strokeWidth={2.25} />
          {/* A camera trigger with no visual cue it's camera-specific reads as just
           *  another grey icon-button in a row of them (Play, Mark breakdown) -- this red
           *  dot is the one thing that says "there's a live feed behind this", the same
           *  recording-indicator language every dashcam/CCTV UI already uses. */}
          <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#DC2626] ring-2 ring-white" />
        </button>
        {open && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-8 z-20 w-72 overflow-hidden rounded-[12px] border border-[rgba(15,23,42,0.08)] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.22)]"
          >
            <div className="flex items-center justify-between border-b border-[rgba(15,23,42,0.06)] px-3 py-2">
              <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#64748B]">
                <Video size={11} className="text-[#0F172A]" />
                Netradyne camera
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded p-0.5 text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
              >
                <X size={14} />
              </button>
            </div>
            <div className="flex gap-1 p-2 pb-0">
              <button
                type="button"
                onClick={() => pick('live')}
                className={clsx(
                  'flex-1 rounded-[8px] px-2 py-1.5 text-[11px] font-semibold transition-colors',
                  tab === 'live' ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]',
                )}
              >
                Live
              </button>
              <button
                type="button"
                disabled={alertId == null}
                onClick={() => pick('clip')}
                title={alertId == null ? 'Open from a Netradyne alert to view its clip' : undefined}
                className={clsx(
                  'flex-1 rounded-[8px] px-2 py-1.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  tab === 'clip' ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]',
                )}
              >
                Clip
              </button>
            </div>
            <div className="p-2">
              {state.loading ? (
                <div className="flex h-[130px] flex-col items-center justify-center gap-2 text-[11px] text-[#94A3B8]">
                  <Loader2 size={18} className="animate-spin" />
                  Connecting…
                </div>
              ) : state.hlsUrl ? (
                <div className="relative overflow-hidden rounded-[8px] bg-black">
                  {tab === 'live' && (
                    <span className="absolute left-1.5 top-1.5 z-10 flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white backdrop-blur-sm">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#DC2626]" />
                      Live
                    </span>
                  )}
                  <video
                    ref={videoRef}
                    controls
                    autoPlay
                    muted
                    className="w-full"
                    style={{ aspectRatio: '16 / 9' }}
                  />
                </div>
              ) : (
                <div className="flex h-[130px] flex-col items-center justify-center gap-1.5 px-3 text-center">
                  <VideoOff size={18} className="text-[#CBD5E1]" />
                  <span className="text-[11px] text-[#64748B]">
                    {state.error ?? (tab === 'clip' ? 'Coming soon' : 'No feed yet')}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
